import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/api";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import StepIndicator from "@/components/StepIndicator";
import { formatUsPhoneDisplay, normalizeUsPhoneDigits } from "@/lib/us-phone";
import { CheckCircle2, Loader2, MailCheck } from "lucide-react";

const planMonthlyPrices: Record<string, number> = { starter: 0, pro: 49, team: 64 };
const companyTypeOptions = [
  { value: "GENERAL_CONTRACTOR_REMODELER", label: "General Contractor (GC) / Remodeler" },
  { value: "PLUMBING_CONTRACTOR", label: "Plumbing Contractor" },
  { value: "ELECTRICAL_CONTRACTOR", label: "Electrical Contractor" },
  { value: "HVAC_CONTRACTOR", label: "HVAC Contractor" },
  {
    value: "LOW_VOLTAGE_SECURITY_ACCESS_CONTROL_CONTRACTOR",
    label: "Low Voltage / Security / Access Control Contractor",
  },
  {
    value: "DRYWALL_FRAMING_CARPENTRY_CONTRACTOR",
    label: "Drywall / Framing / Carpentry Contractor",
  },
  { value: "FLOORING_CONTRACTOR", label: "Flooring Contractor" },
  { value: "PAINTING_CONTRACTOR", label: "Painting Contractor" },
  { value: "CONCRETE_CONTRACTOR", label: "Concrete Contractor" },
  { value: "ROOFING_CONTRACTOR", label: "Roofing Contractor" },
  {
    value: "LANDSCAPING_HARDSCAPING_CONTRACTOR",
    label: "Landscaping / Hardscaping Contractor",
  },
] as const;

type AppliedPromo = {
  promoCodeId: string;
  code: string;
  type: string;
  benefitType?: "DISCOUNT" | "GRANT";
  targetPlan: string | null;
  targetPlanLabel: string;
  benefitSummary: string;
  durationMonths: number | null;
  grantDurationMonths?: number | null;
  originalPrice: number;
  finalPrice: number;
  interval: "MONTHLY" | "YEARLY";
  checkoutRequired?: boolean;
  firstBillingCycleOnly: boolean;
  adminNote: string | null;
  expiresAt: string | null;
  status: "Active" | "Redeemed" | "Expired" | "Revoked";
  message: string;
};

type CheckoutPreviewResponse = {
  originalAmount: number;
  discountAmount: number;
  finalAmount: number;
  checkoutRequired: boolean;
  executionPath: "STRIPE_CHECKOUT" | "INTERNAL_FINALIZE";
  promoOutcome: "NONE" | "DISCOUNT" | "GRANT";
  message: string;
  appliedPromo: AppliedPromo | null;
};

export default function CreateAccount() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const plan = searchParams.get("plan") || "starter";
  const interval =
    plan === "starter"
      ? "MONTHLY"
      : searchParams.get("interval") === "YEARLY"
        ? "YEARLY"
        : "MONTHLY";
  const monthlyPrice = planMonthlyPrices[plan] ?? 0;
  const effectiveMonthlyPrice = interval === "YEARLY" ? Math.round(monthlyPrice * 0.8) : monthlyPrice;
  const annualTotal = effectiveMonthlyPrice * 12;
  const planPriceLabel =
    interval === "YEARLY"
      ? `$${effectiveMonthlyPrice}/mo`
      : `$${monthlyPrice}/mo`;

  const [form, setForm] = useState({
    companyName: "",
    companyType: "GENERAL_CONTRACTOR_REMODELER",
    fullName: "",
    email: "",
    password: "",
    phone: "",
    address: "",
    role: "",
    agreed: false,
    smsResponsibilityConfirmed: false,
  });
  const [couponCode, setCouponCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<AppliedPromo | null>(null);
  const [promoError, setPromoError] = useState("");
  const [promoApplying, setPromoApplying] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [pendingSignupId, setPendingSignupId] = useState<string | null>(null);
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(null);
  const [verifiedAt, setVerifiedAt] = useState<string | null>(null);
  const resendTimerRef = useRef<number | null>(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("workscope-signup-draft");
      if (!raw) return;

      const draft = JSON.parse(raw) as {
        plan?: string;
        interval?: string;
        companyName?: string;
        companyType?: string;
        fullName?: string;
        email?: string;
        password?: string;
        phone?: string;
        address?: string;
        role?: string;
        agreed?: boolean;
        smsResponsibilityConfirmed?: boolean;
        couponCode?: string;
        appliedPromo?: AppliedPromo | null;
        pendingSignupId?: string | null;
        emailVerifiedAt?: string | null;
      };

      const normalizedDraftInterval =
        draft.plan === "starter"
          ? "MONTHLY"
          : draft.interval === "YEARLY"
            ? "YEARLY"
            : "MONTHLY";

      if ((draft.plan ?? "starter") !== plan || normalizedDraftInterval !== interval) {
        return;
      }

      setForm((current) => ({
        ...current,
        companyName: draft.companyName ?? current.companyName,
        companyType: draft.companyType ?? current.companyType,
        fullName: draft.fullName ?? current.fullName,
        email: draft.email ?? current.email,
        password: draft.password ?? current.password,
        phone: formatUsPhoneDisplay(draft.phone ?? current.phone),
        address: draft.address ?? current.address,
        role: draft.role ?? current.role,
        agreed: typeof draft.agreed === "boolean" ? draft.agreed : current.agreed,
        smsResponsibilityConfirmed:
          typeof draft.smsResponsibilityConfirmed === "boolean"
            ? draft.smsResponsibilityConfirmed
            : current.smsResponsibilityConfirmed,
      }));
      setCouponCode(draft.couponCode ?? "");
      setAppliedPromo(draft.appliedPromo ?? null);
      setPendingSignupId(draft.pendingSignupId ?? null);
      if (draft.email && draft.emailVerifiedAt) {
        setVerifiedEmail(draft.email.trim().toLowerCase());
        setVerifiedAt(draft.emailVerifiedAt);
      }
    } catch {
      // Ignore malformed local draft and continue with a fresh form.
    }
  }, [interval, plan]);

  const update = (field: string, value: string | boolean) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: "" }));
    if (field === "email" && typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (!verifiedEmail || normalized !== verifiedEmail) {
        setVerifiedEmail(null);
        setVerifiedAt(null);
      }
    }
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};

    if (!form.companyName.trim()) nextErrors.companyName = t("common.required");
    if (!form.companyType.trim()) nextErrors.companyType = t("common.required");
    if (!form.fullName.trim()) nextErrors.fullName = t("common.required");
    if (!form.email.trim() || !form.email.includes("@")) nextErrors.email = t("createAccount.validEmailRequired");
    if (form.password.length < 6) nextErrors.password = t("createAccount.passwordMin");
    if (!form.agreed) nextErrors.agreed = t("createAccount.mustAgree");
    if (!form.smsResponsibilityConfirmed) {
      nextErrors.smsResponsibilityConfirmed = t("createAccount.smsResponsibilityRequired");
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const normalizedEmail = useMemo(() => form.email.trim().toLowerCase(), [form.email]);
  const emailIsVerified = Boolean(verifiedEmail && verifiedEmail === normalizedEmail && verifiedAt);
  const defaultSubtotal = plan === "starter" ? monthlyPrice : interval === "YEARLY" ? annualTotal : monthlyPrice;
  const effectiveSubtotal = appliedPromo ? appliedPromo.finalPrice : defaultSubtotal;

  useEffect(() => {
    return () => {
      if (resendTimerRef.current) {
        window.clearInterval(resendTimerRef.current);
      }
    };
  }, []);

  const startResendCountdown = (seconds = 30) => {
    if (resendTimerRef.current) {
      window.clearInterval(resendTimerRef.current);
    }
    setResendCountdown(seconds);
    resendTimerRef.current = window.setInterval(() => {
      setResendCountdown((current) => {
        if (current <= 1) {
          if (resendTimerRef.current) {
            window.clearInterval(resendTimerRef.current);
            resendTimerRef.current = null;
          }
          return 0;
        }
        return current - 1;
      });
    }, 1000);
  };

  const persistSignupDraft = (nextPendingSignupId: string, nextVerifiedAt?: string | null) => {
    sessionStorage.setItem(
      "workscope-signup-draft",
      JSON.stringify({
        plan,
        interval,
        couponCode,
        appliedPromo,
        pendingSignupId: nextPendingSignupId,
        emailVerifiedAt: nextVerifiedAt ?? null,
        expiresAt: null,
        ...form,
      }),
    );
  };

  const requestOtp = async (targetPendingSignupId: string) => {
    setOtpSending(true);
    setOtpError("");
    try {
      await apiRequest<{ success: boolean; expiresAt: string; resendAvailableInSeconds: number }>(
        "/auth/otp/send",
        {
          method: "POST",
          auth: false,
          body: {
            email: normalizedEmail,
            pendingSignupId: targetPendingSignupId,
          },
        },
      );
      startResendCountdown(30);
      setVerificationModalOpen(true);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t("createAccount.unableToSendCode");
      setErrors((current) => ({
        ...current,
        email: t("createAccount.unableToSendCode"),
      }));
      toast({
        title: t("createAccount.unableToSendCodeTitle"),
        description: message,
        variant: "destructive",
      });
    } finally {
      setOtpSending(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const response = await apiRequest<{
        pendingSignupId: string;
        planId: string;
        status: string;
        expiresAt: string;
      }>("/auth/pending-signups", {
        method: "POST",
        auth: false,
        credentials: "include",
        body: {
          companyName: form.companyName,
          companyType: form.companyType,
          fullName: form.fullName,
          email: form.email,
          password: form.password,
          phone: normalizeUsPhoneDigits(form.phone) || undefined,
          address: form.address || undefined,
          role: form.role || undefined,
          planId: plan,
          planInterval: interval,
          promoCode: appliedPromo?.code || undefined,
          agreed: form.agreed,
        },
      });

      setPendingSignupId(response.pendingSignupId);
      if (emailIsVerified) {
        persistSignupDraft(response.pendingSignupId, verifiedAt);
        navigate(`/payment?plan=${plan}&interval=${interval}&signup=${response.pendingSignupId}`);
      } else {
        persistSignupDraft(response.pendingSignupId, null);
        setOtpCode("");
        await requestOtp(response.pendingSignupId);
      }
    } catch (error) {
      toast({
        title: t("createAccount.unableToContinue"),
        description: error instanceof Error ? error.message : t("common.tryAgain"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const verifyAndContinue = async () => {
    if (!pendingSignupId || otpCode.length !== 4 || otpVerifying) return;

    setOtpVerifying(true);
    setOtpError("");
    try {
      const response = await apiRequest<{ success: boolean; verifiedAt: string }>("/auth/otp/verify", {
        method: "POST",
        auth: false,
        body: {
          email: normalizedEmail,
          pendingSignupId,
          code: otpCode,
        },
      });
      setVerifiedEmail(normalizedEmail);
      setVerifiedAt(response.verifiedAt);
      persistSignupDraft(pendingSignupId, response.verifiedAt);
      setVerificationModalOpen(false);
      navigate(`/payment?plan=${plan}&interval=${interval}&signup=${pendingSignupId}`);
    } catch (error) {
      setOtpError(error instanceof Error ? error.message : t("createAccount.wrongCode"));
    } finally {
      setOtpVerifying(false);
    }
  };

  const resendCode = async () => {
    if (!pendingSignupId || resendCountdown > 0 || otpSending) return;
    setOtpCode("");
    await requestOtp(pendingSignupId);
  };

  const applyPromoCode = async () => {
    const normalizedCode = couponCode.trim().toUpperCase();
    if (!normalizedCode || promoApplying) return;

    setPromoApplying(true);
    setPromoError("");
    try {
      const response = await apiRequest<CheckoutPreviewResponse>("/auth/checkout-preview", {
        method: "POST",
        auth: false,
        body: {
          promoCode: normalizedCode,
          planId: plan,
          planInterval: interval,
        },
      });

      if (!response.appliedPromo) {
        throw new Error(t("createAccount.unableToApplyPromo"));
      }

      setAppliedPromo(response.appliedPromo);
      setCouponCode(response.appliedPromo.code);
      toast({
        title: t("createAccount.promoAppliedTitle"),
        description: response.message || response.appliedPromo.benefitSummary,
      });
    } catch (error) {
      setAppliedPromo(null);
      setPromoError(error instanceof Error ? error.message : t("createAccount.unableToApplyPromo"));
    } finally {
      setPromoApplying(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-background">
        <StepIndicator currentStep={2} />
        <div className="container mx-auto px-4 pb-20 lg:px-8">
          <div className="mx-auto grid max-w-4xl gap-10 lg:grid-cols-3">
            <form onSubmit={submit} className="space-y-5 lg:col-span-2">
              <h1 className="text-2xl font-bold text-primary">{t("createAccount.title")}</h1>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="companyName" className="text-[#0C1F4F]">
                    {t("createAccount.companyName")} <span className="text-accent">*</span>
                  </Label>
                  <Input
                    id="companyName"
                    value={form.companyName}
                    onChange={(e) => update("companyName", e.target.value)}
                    placeholder="North Shore Builders"
                    className={errors.companyName ? "border-destructive" : ""}
                  />
                  {errors.companyName ? <p className="mt-1 text-xs text-destructive">{errors.companyName}</p> : null}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="companyType" className="text-[#0C1F4F]">
                    {t("createAccount.companyType")} <span className="text-accent">*</span>
                  </Label>
                  <Select value={form.companyType} onValueChange={(value) => update("companyType", value)}>
                    <SelectTrigger id="companyType" className={errors.companyType ? "border-destructive" : ""}>
                      <SelectValue placeholder={t("createAccount.selectCompanyType")} />
                    </SelectTrigger>
                    <SelectContent>
                      {companyTypeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.companyType ? <p className="mt-1 text-xs text-destructive">{errors.companyType}</p> : null}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <div className="flex min-h-6 items-center">
                    <Label htmlFor="fullName" className="text-[#0C1F4F]">
                      {t("createAccount.yourName")} <span className="text-accent">*</span>
                    </Label>
                  </div>
                  <Input
                    id="fullName"
                    value={form.fullName}
                    onChange={(e) => update("fullName", e.target.value)}
                    placeholder="Alex Carter"
                    className={errors.fullName ? "border-destructive" : ""}
                  />
                  {errors.fullName ? <p className="mt-1 text-xs text-destructive">{errors.fullName}</p> : null}
                </div>
                <div className="space-y-1.5">
                  <div className="flex min-h-6 items-center justify-between gap-3">
                    <Label htmlFor="email" className="text-[#0C1F4F]">
                      {t("login.email")} <span className="text-accent">*</span>
                    </Label>
                    {emailIsVerified ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        {t("createAccount.verified")}
                      </span>
                    ) : null}
                  </div>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="alex@northshorebuilders.com"
                    className={errors.email ? "border-destructive" : ""}
                  />
                  {errors.email ? <p className="mt-1 text-xs text-destructive">{errors.email}</p> : null}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-[#0C1F4F]">
                  {t("login.password")} <span className="text-accent">*</span>
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={form.password}
                  onChange={(e) => update("password", e.target.value)}
                  placeholder="Choose a password with at least 8 characters"
                  className={errors.password ? "border-destructive" : ""}
                />
                {errors.password ? <p className="mt-1 text-xs text-destructive">{errors.password}</p> : null}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="phone" className="text-[#0C1F4F]">
                    {t("createAccount.phone")} <span className="text-muted-foreground">({t("common.optional")})</span>
                  </Label>
                  <Input
                    id="phone"
                    value={form.phone}
                    inputMode="numeric"
                    maxLength={14}
                    onChange={(e) => update("phone", formatUsPhoneDisplay(e.target.value))}
                    placeholder="(555) 111-2222"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="role" className="text-[#0C1F4F]">
                    {t("createAccount.role")} <span className="text-muted-foreground">({t("common.optional")})</span>
                  </Label>
                  <Input
                    id="role"
                    value={form.role}
                    onChange={(e) => update("role", e.target.value)}
                    placeholder="Owner / Admin / Project Manager"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="address" className="text-[#0C1F4F]">
                  {t("createAccount.address")} <span className="text-muted-foreground">({t("common.optional")})</span>
                </Label>
                <Input
                  id="address"
                  value={form.address}
                  onChange={(e) => update("address", e.target.value)}
                  placeholder="123 Main St, Miami, FL 33101"
                />
              </div>

              <div className="flex items-start gap-2">
                <Checkbox id="agreed" checked={form.agreed} onCheckedChange={(value) => update("agreed", !!value)} />
                <Label htmlFor="agreed" className="text-sm leading-snug text-muted-foreground">
                  {t("createAccount.agreePrefix")}{" "}
                  <Link to="/terms" className="text-primary underline">
                    {t("createAccount.terms")}
                  </Link>{" "}
                  {t("createAccount.and")}{" "}
                  <Link to="/privacy" className="text-primary underline">
                    {t("createAccount.privacy")}
                  </Link>
                  .
                </Label>
              </div>
              {errors.agreed ? <p className="text-xs text-destructive">{errors.agreed}</p> : null}

              <div className="space-y-1.5">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="smsResponsibilityConfirmed"
                    checked={form.smsResponsibilityConfirmed}
                    onCheckedChange={(value) =>
                      update("smsResponsibilityConfirmed", !!value)
                    }
                  />
                  <Label
                    htmlFor="smsResponsibilityConfirmed"
                    className="text-sm leading-snug text-muted-foreground"
                  >
                    {t("createAccount.smsResponsibilityLabel")}{" "}
                    {t("createAccount.smsResponsibilityLinksPrefix")}{" "}
                    <Link to="/sms-consent" className="text-primary underline">
                      {t("createAccount.smsConsent")}
                    </Link>
                    {" "}
                    {t("createAccount.and")}{" "}
                    <Link to="/sms-opt-in-proof" className="text-primary underline">
                      {t("createAccount.smsOptInProof")}
                    </Link>
                    {t("createAccount.smsResponsibilityLinksSuffix")}
                  </Label>
                </div>
              </div>
              {errors.smsResponsibilityConfirmed ? (
                <p className="text-xs text-destructive">
                  {errors.smsResponsibilityConfirmed}
                </p>
              ) : null}

              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting || otpSending}
                className="w-full bg-accent text-accent-foreground hover:bg-accent/90 sm:w-auto"
              >
                {isSubmitting || otpSending ? t("common.preparing") : t("createAccount.continueToPayment")}
              </Button>
            </form>

            <div className="h-fit rounded-xl border border-border bg-card p-6 shadow-sm">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t("createAccount.yourPlan")}</h3>
              <p className="mt-2 text-xl font-bold text-primary">{t(`plans.${plan}`)}</p>
              <p className="mt-1 text-2xl font-extrabold text-accent">{planPriceLabel}</p>
              <p className="mt-1 text-xs font-medium text-muted-foreground">
                {plan === "starter"
                  ? t("createAccount.billedForOneMonth")
                  : interval === "YEARLY"
                  ? t("pricing.billedForOneYear", { amount: `$${annualTotal}` })
                  : t("createAccount.billedMonthly")}
              </p>
              <div className="mt-4 space-y-1.5">
                <Label htmlFor="couponCode" className="text-[#0C1F4F]">
                  {t("createAccount.promoCode")}
                </Label>
                <Input
                  id="couponCode"
                  value={couponCode}
                  onChange={(e) => {
                    setCouponCode(e.target.value);
                    setPromoError("");
                    setAppliedPromo(null);
                  }}
                  placeholder={t("createAccount.promoPlaceholder")}
                />
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-muted-foreground">
                    {t("createAccount.promoHelp")}
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="border-accent text-accent hover:bg-accent/10"
                    onClick={() => void applyPromoCode()}
                    disabled={!couponCode.trim() || promoApplying}
                  >
                    {promoApplying ? t("common.applying") : appliedPromo?.code === couponCode.trim().toUpperCase() ? t("common.applied") : t("pages.upgradePlan.apply")}
                  </Button>
                </div>
                {promoError ? <p className="text-xs text-destructive">{promoError}</p> : null}
                {appliedPromo ? (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm">
                    <div className="font-semibold text-emerald-700">{t("createAccount.promoApplied", { code: appliedPromo.code })}</div>
                    <div className="mt-1 text-emerald-700/90">{appliedPromo.benefitSummary}</div>
                    {appliedPromo.checkoutRequired === false ? (
                      <div className="mt-1 text-emerald-700/90">
                        {appliedPromo.benefitType === "GRANT"
                          ? t("createAccount.promoGrantNoCheckout")
                          : t("createAccount.promoNoCheckout")}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
              {appliedPromo ? (
                <div className="mt-4 space-y-2 rounded-lg border border-border/60 bg-muted/10 p-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("createAccount.originalPrice")}</span>
                    <span className="font-medium text-card-foreground">${appliedPromo.originalPrice.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("createAccount.promoCode")}</span>
                    <span className="font-medium text-emerald-700">{appliedPromo.code}</span>
                  </div>
                  <div className="flex justify-between border-t border-border/60 pt-2">
                    <span className="font-medium text-primary">{t("createAccount.updatedPrice")}</span>
                    <span className="font-semibold text-accent">${effectiveSubtotal.toFixed(2)}</span>
                  </div>
                </div>
              ) : null}
              <p className="mt-3 text-xs text-muted-foreground">{t("createAccount.changePlansLater")}</p>
            </div>
          </div>
        </div>
      </div>
      <Dialog open={verificationModalOpen} onOpenChange={(open) => !open && setVerificationModalOpen(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0C1F4F]">{t("createAccount.verifyEmailTitle")}</DialogTitle>
            <DialogDescription className="text-[#0C1F4F]/70">
              {t("createAccount.verifyEmailCopy", { email: form.email })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-lg border border-border/60 bg-muted/15 px-4 py-3">
              <div className="rounded-full bg-accent/10 p-2 text-accent">
                <MailCheck className="h-4 w-4" />
              </div>
              <div className="text-sm text-[#0C1F4F]/80">
                {t("createAccount.latestCodeHint")}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#0C1F4F]">{t("createAccount.verificationCode")}</Label>
              <InputOTP
                autoFocus
                maxLength={4}
                value={otpCode}
                onChange={(value) => {
                  const digits = value.replace(/\D/g, "").slice(0, 4);
                  setOtpCode(digits);
                  setOtpError("");
                }}
                pattern={/^\d+$/}
                disabled={otpSending || otpVerifying}
                containerClassName="justify-center"
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} className="h-14 w-12 rounded-l-xl border-[#F38C16]/35 text-lg font-semibold text-[#0C1F4F]" />
                  <InputOTPSlot index={1} className="h-14 w-12 border-[#F38C16]/35 text-lg font-semibold text-[#0C1F4F]" />
                  <InputOTPSlot index={2} className="h-14 w-12 border-[#F38C16]/35 text-lg font-semibold text-[#0C1F4F]" />
                  <InputOTPSlot index={3} className="h-14 w-12 rounded-r-xl border-[#F38C16]/35 text-lg font-semibold text-[#0C1F4F]" />
                </InputOTPGroup>
              </InputOTP>
              {otpError ? <p className="text-sm text-destructive">{otpError}</p> : null}
            </div>

            <div className="flex items-center justify-between gap-3 text-sm">
              <button
                type="button"
                onClick={() => void resendCode()}
                disabled={resendCountdown > 0 || otpSending || otpVerifying}
                className="font-medium text-accent disabled:cursor-not-allowed disabled:text-muted-foreground"
              >
                {resendCountdown > 0 ? t("createAccount.resendIn", { seconds: resendCountdown }) : t("createAccount.resendCode")}
              </button>
              {otpSending ? (
                <span className="inline-flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t("login.sending")}
                </span>
              ) : null}
            </div>
          </div>

          <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setVerificationModalOpen(false)}
              disabled={otpSending || otpVerifying}
            >
              {t("createAccount.cancel")}
            </Button>
            <Button
              onClick={() => void verifyAndContinue()}
              disabled={otpCode.length !== 4 || otpSending || otpVerifying}
              className="bg-accent text-accent-foreground hover:bg-accent/90"
            >
              {otpVerifying ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("createAccount.verifying")}
                </>
              ) : (
                t("createAccount.verifyAndContinue")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Footer />
    </>
  );
}
